/**
 * Visually-confusable character normalisation for answer checking.
 *
 * WHERE THE MAP LIVES
 * -------------------
 * `CONFUSABLE_PAIRS` below is the single source of truth for the confusable
 * fold. It is consumed only by `normalizeConfusables`, which typing and
 * tap-to-answer correctness checking apply to BOTH the expected answer and the
 * learner's answer before comparison (see `score.ts`). Keep the map here and
 * nowhere else — do not inline codepoints elsewhere.
 *
 * KEEP IN SYNC WITH iOS / ANDROID
 * -------------------------------
 * This map is shared, character-for-character, across the web, iOS and Android
 * clients. Do NOT add or drop pairs here without making the identical change in
 * the other two clients, otherwise the same answer grades differently per
 * platform.
 *
 * HOW TO ADD A NEW PAIR SAFELY
 * ----------------------------
 * Add a `[variant, canonical]` entry that folds the VARIANT codepoint TO its
 * CANONICAL representative. The map must stay flat, order-independent and
 * idempotent, so the golden rule is: a canonical target must NEVER also appear
 * as a key. Never map a character to something that is itself a key, and never
 * add a key whose value is already used as a key. Following that rule keeps a
 * single pass sufficient to fully normalise any input.
 */

// [variant, canonical] codepoint pairs. Variant folds to canonical.
const CONFUSABLE_PAIRS: ReadonlyArray<readonly [number, number]> = [
  [0x00a1, 0x0069], // ¡ -> i
  [0x30fc, 0x4e00], // ー (katakana chōonpu) -> 一 (kanji ichi)
  [0x0030, 0x004f], // 0 -> O
  [0x30ed, 0x53e3], // ロ -> 口
  [0x30a8, 0x5de5], // エ -> 工
  [0x30cb, 0x4e8c], // ニ -> 二
  [0x672b, 0x672a], // 末 -> 未
  [0x58eb, 0x571f], // 士 -> 土
  [0x5165, 0x4eba], // 入 -> 人
  // Cyrillic -> Latin lowercase
  [0x0430, 0x0061], // а -> a
  [0x0435, 0x0065], // е -> e
  [0x043e, 0x006f], // о -> o
  [0x0440, 0x0070], // р -> p
  [0x0441, 0x0063], // с -> c
  [0x0443, 0x0079], // у -> y
  [0x0445, 0x0078], // х -> x
  // Greek -> Latin uppercase
  [0x0391, 0x0041], // Α -> A
  [0x0392, 0x0042], // Β -> B
  [0x0395, 0x0045], // Ε -> E
  [0x0399, 0x0049], // Ι -> I
  [0x039f, 0x004f], // Ο -> O
  [0x03a1, 0x0050], // Ρ -> P
  [0x03a7, 0x0058], // Χ -> X
  [0x03bd, 0x0076], // ν -> v
];

const CONFUSABLE_MAP: ReadonlyMap<string, string> = new Map(
  CONFUSABLE_PAIRS.map(([variant, canonical]) => [
    String.fromCodePoint(variant),
    String.fromCodePoint(canonical),
  ]),
);

/**
 * Folds every visually-confusable variant character in `input` to its canonical
 * representative in a single pass. Characters absent from the map are left
 * untouched, so exact-match behaviour for non-mapped characters is unchanged.
 */
export const normalizeConfusables = (input: string): string => {
  let normalized = '';
  // Iterate by codepoint so surrogate pairs are never split mid-character.
  for (const character of input) {
    normalized += CONFUSABLE_MAP.get(character) ?? character;
  }
  return normalized;
};
